import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';

const roleOptions = [
  { value: 'hr', label: 'HR' },
  { value: 'hod', label: 'HOD' },
  { value: 'employee', label: 'Employee' },
  { value: 'coo', label: 'COO' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'cos', label: 'COS'}
];

const UpdateEmployee = () => {
  const baseUrl = API_BASE_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const employeeId = location.state?.employeeId || id;
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingDepartments, setFetchingDepartments] = useState(true);
  const [fetchingEmployee, setFetchingEmployee] = useState(true);
  const [form, setForm] = useState({
    code: '',
    first_name: '',
    last_name: '',
    department: null,
    secondary_department: null,
    national_id: '',
    role: '',
    position: '',
    occupation: ''
  });

  useEffect(() => {
    const fetchDepartments = async () => {
      setFetchingDepartments(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/departments/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        setDepartments(response.data || []);
      } catch (err) {
        toast.error('Failed to fetch departments.');
      } finally {
        setFetchingDepartments(false);
      }
    };
    fetchDepartments();
  }, [baseUrl]);

  useEffect(() => {
    const fetchEmployee = async () => {
      setFetchingEmployee(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const emp = response.data.employee;
        setForm({
          code: emp.code || '',
          first_name: emp.first_name || '',
          last_name: emp.last_name || '',
          department: emp.department ? { value: emp.department, label: emp.department_name ? `${emp.department_name}${emp.course_center_name ? ` (${emp.course_center_name})` : ''}` : '' } : null,
          secondary_department: emp.secondary_department ? { value: emp.secondary_department, label: emp.secondary_department_name ? `${emp.secondary_department_name}${emp.course_center_name ? ` (${emp.course_center_name})` : ''}` : '' } : null,
          national_id: emp.national_id || '',
          role: emp.user?.role ? { value: emp.user.role, label: emp.user.role.toUpperCase() } : null,
          position: emp.position || '',
          occupation: emp.occupation || ''
        });
      } catch (err) {
        toast.error('Failed to fetch employee data.');
      } finally {
        setFetchingEmployee(false);
      }
    };
    if (employeeId) fetchEmployee();
  }, [baseUrl, employeeId]);

  const departmentOptions = departments.map(dep => ({
    value: dep.id,
    label: dep.course_center_name ? `${dep.name} (${dep.course_center_name})` : dep.name
  }));

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (selected, { name }) => {
    setForm({ ...form, [name]: selected });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = Cookies.get('token');
      await axios.put(`${baseUrl}/api/employees/${employeeId}/`, {
        code: form.code,
        first_name: form.first_name,
        last_name: form.last_name,
        department: form.department?.value,
        secondary_department: form.secondary_department?.value,
        national_id: form.national_id,
        role: form.role?.value,
        position: form.position,
        occupation: form.occupation
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Employee updated successfully!');
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update employee.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Update Employee</h4>
              <p className="text-muted mb-0 fs-13">Modify employee personal and organizational details</p>
            </div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            >
              <i className="ti ti-arrow-left" /> Back
            </button>
          </div>

          {/* Form Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light border-bottom py-3">
              <h6 className="card-title mb-0 fw-bold text-dark">
                Employee Details Form
              </h6>
            </div>
            <div className="card-body">
              {fetchingEmployee ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="fs-13 mb-0">Loading employee data...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-medium">
                        Employee Code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="code"
                        value={form.code}
                        onChange={handleChange}
                        required
                        className="form-control"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-medium">
                        Primary Department <span className="text-danger">*</span>
                      </label>
                      <Select
                        name="department"
                        value={form.department}
                        onChange={handleSelectChange}
                        options={departmentOptions}
                        placeholder={fetchingDepartments ? 'Loading departments...' : 'Select department'}
                        classNamePrefix="react-select"
                        isDisabled={loading || fetchingDepartments}
                        required
                      />
                    </div>

                    {form.role?.value === 'hod' && (
                      <div className="col-md-6 col-lg-4">
                        <label className="form-label fw-medium">Secondary Department (Optional)</label>
                        <Select
                          name="secondary_department"
                          value={form.secondary_department}
                          onChange={handleSelectChange}
                          options={departmentOptions}
                          placeholder={fetchingDepartments ? 'Loading departments...' : 'Select department'}
                          classNamePrefix="react-select"
                          isDisabled={loading || fetchingDepartments}
                        />
                      </div>
                    )}

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={form.first_name}
                        onChange={handleChange}
                        required
                        className="form-control"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={form.last_name}
                        onChange={handleChange}
                        required
                        className="form-control"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        National ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="national_id"
                        value={form.national_id}
                        onChange={handleChange}
                        required
                        className="form-control"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        Role <span className="text-danger">*</span>
                      </label>
                      <Select
                        name="role"
                        value={form.role}
                        onChange={handleSelectChange}
                        options={roleOptions}
                        isDisabled={loading || fetchingEmployee}
                        classNamePrefix="react-select"
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        Occupation <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="occupation"
                        value={form.occupation}
                        onChange={handleChange}
                        required
                        className="form-control"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        Position <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="position"
                        value={form.position}
                        onChange={handleChange}
                        required
                        className="form-control"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-end gap-2 mt-4 pt-3 border-top">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="btn btn-secondary"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary d-flex align-items-center gap-2"
                      disabled={loading || !form.code || !form.first_name || !form.last_name || !form.department || !form.national_id || !form.position || !form.occupation}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          Updating Employee...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-device-floppy" /> Update Employee
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpdateEmployee;
